import type { Claim } from "../domain/run.ts";
import type { WorkPackage } from "../domain/plan.ts";

/** Mock data until the APIs land: run 0147, "Checkout v2", on checkout-service. Matches the design boards. */
export const REPO = { org: "acme", name: "checkout-service", url: "https://git.example.com/acme/checkout-service.git", branch: "main" };
export const USER = { firstName: "Ryan", initials: "RJ", email: "ryan@refuelcreative.com.au" };
export const RUN = { id: "0147", title: "Checkout v2", cap: "$20", estimate: "$14–19" };
export const REPOS = [{ name: "checkout-service", active: true }, { name: "marketing-site" }] as const;

export const SPEC_TEXT = `# Checkout v2

## 1. Goals
Show GST-inclusive totals everywhere a price appears, and replace the payment form.

## 2. Totals
2.1 Prices are stored in cents, ex GST.
2.2 Apply discounts before GST. Round half up to the cent.

## 3. Payment UI
3.1 New card form with inline validation.
3.2 Saved cards for signed-in customers.

## 4. Webhooks
4.1 Handle payment succeeded, failed and refunded events idempotently.

## Acceptance
- Totals match the finance sheet to the cent for all 40 sample carts.
- Checkout e2e passes on mobile and desktop.`;

export const PACKAGES: readonly WorkPackage[] = [
  { n: 1, title: "Schema", spec: "Spec §2.1", ground: ["db/schema/**", "db/migrations/0042_*"], agent: 8, after: [] },
  { n: 2, title: "Totals", spec: "Spec §2.2", ground: ["src/checkout/**", "lib/money.ts"], agent: 1, after: [] },
  { n: 3, title: "Discounts", spec: "Spec §2.2", ground: ["src/checkout/discounts.ts", "lib/money.ts"], agent: 3, after: [2] },
  { n: 4, title: "Payment UI", spec: "Spec §3.1", ground: ["src/payments/ui/**", "ui/form.tsx", "lib/money.ts"], agent: 5, after: [3] },
  { n: 5, title: "Saved cards", spec: "Spec §3.2", ground: ["src/payments/saved/**", "ui/form.tsx"], agent: 2, after: [4] },
  { n: 6, title: "Webhooks", spec: "Spec §4.1", ground: ["src/webhooks/**", "tests/webhooks/**"], agent: 10, after: [] },
  { n: 7, title: "Refunds", spec: "Spec §4.1", ground: ["src/refunds/**"], agent: 4, after: [] },
  { n: 8, title: "Receipts", spec: "Spec §1", ground: ["src/email/receipts/**"], agent: 6, after: [] },
  { n: 9, title: "Tax reports", spec: "Spec §1", ground: ["src/reports/tax/**"], agent: 7, after: [] },
  { n: 10, title: "Mobile layout", spec: "Spec §3", ground: ["src/mobile/**"], agent: 9, after: [] },
  { n: 11, title: "E2E tests", spec: "Acceptance", ground: ["tests/e2e/**"], agent: 11, after: [] },
  { n: 12, title: "Docs", spec: "Spec §1", ground: ["docs/**"], agent: 12, after: [] },
];

export const AGENT_NAMES: Readonly<Record<number, string>> = { 1: "Claude Code · ryan-mbp", 2: "Codex CLI · build-box-2" };

/** Live run at 18:42. Status per agent, as on the design board. */
export const LIVE = {
  elapsed: "18:42",
  cost: "$4.82",
  landed: "3 / 12",
  lastUpdate: "14:07",
  agents: [
    { n: 1, wp: "WP-2 Totals", status: "verified", working: true },
    { n: 2, wp: "WP-5 Saved cards", status: "neutral", label: "Queued", working: false },
    { n: 3, wp: "WP-3 Discounts", status: "claimed", working: true },
    { n: 4, wp: "WP-7 Refunds", status: "claimed", working: true },
    { n: 5, wp: "WP-4 Payment UI", status: "negotiating", working: true },
    { n: 6, wp: "WP-8 Receipts", status: "failed", working: true },
    { n: 7, wp: "WP-9 Tax reports", status: "claimed", working: true },
    { n: 8, wp: "WP-1 Schema", status: "landed", working: false },
    { n: 9, wp: "WP-10 Mobile layout", status: "negotiating", working: true },
    { n: 10, wp: "WP-6 Webhooks", status: "verified", working: true },
    { n: 11, wp: "WP-11 E2E tests", status: "claimed", working: true },
    { n: 12, wp: "WP-12 Docs", status: "landed", working: false },
  ],
} as const;

/** Claims with the file count used to size the treemap tiles. */
export const CLAIMS: ReadonlyArray<Claim & { files: number }> = [
  { path: "src/checkout/**", agent: 1, wp: 2, status: "verified", files: 38 },
  { path: "src/payments/ui/**", agent: 5, wp: 4, status: "negotiating", files: 30 },
  { path: "ui/styles.css", agent: 5, wp: 4, status: "overlapping", overlapWith: [9], files: 6 },
  { path: "src/mobile/**", agent: 9, wp: 10, status: "negotiating", files: 18 },
  { path: "discounts.ts", agent: 3, wp: 3, status: "claimed", files: 8 },
  { path: "lib/money.ts", agent: 1, wp: 2, status: "verified", files: 3 },
  { path: "db/schema/**", agent: 8, wp: 1, status: "landed", files: 16 },
  { path: "src/payments/saved/**", agent: 2, wp: 5, status: "neutral", files: 14 },
  { path: "src/webhooks/**", agent: 10, wp: 6, status: "verified", files: 14 },
  { path: "src/refunds/**", agent: 4, wp: 7, status: "claimed", files: 12 },
  { path: "src/email/receipts/**", agent: 6, wp: 8, status: "failed", files: 12 },
  { path: "src/reports/tax/**", agent: 7, wp: 9, status: "claimed", files: 12 },
  { path: "tests/e2e/**", agent: 11, wp: 11, status: "claimed", files: 20 },
  { path: "docs/**", agent: 12, wp: 12, status: "landed", files: 9 },
];

export const UNCLAIMED = [{ path: "config/", files: 6 }, { path: "scripts/", files: 5 }, { path: "public/", files: 5 }] as const;

/** The map at 13:47:12, for replay. AX-03 has just re-claimed discounts.ts. */
export const REPLAY_CLAIMS: ReadonlyArray<Claim & { files: number }> = [
  { path: "src/checkout/**", agent: 1, wp: 2, status: "verified", files: 38 },
  { path: "src/payments/ui/**", agent: 5, wp: 4, status: "claimed", files: 30 },
  { path: "discounts.ts", agent: 3, wp: 3, status: "claimed", files: 8 },
  { path: "lib/money.ts", agent: 1, wp: 2, status: "verified", files: 3 },
  { path: "db/schema/**", agent: 8, wp: 1, status: "landed", files: 16 },
  { path: "src/webhooks/**", agent: 10, wp: 6, status: "claimed", files: 14 },
  { path: "src/refunds/**", agent: 4, wp: 7, status: "claimed", files: 12 },
  { path: "src/email/receipts/**", agent: 6, wp: 8, status: "claimed", files: 12 },
];
export const REPLAY_UNCLAIMED = [{ path: "tests/e2e/**", files: 20 }, { path: "src/mobile/**", files: 18 }, { path: "docs/**", files: 9 }] as const;
